import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useParseCvForCandidate, useUploadCandidateFile } from '@/hooks/use-candidates';
import { useCandidateParseJob } from '../context/candidate-parse-job-context';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import type { CandidateFileType } from '@/types/candidates';
import { UploadCloud } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FILE_TYPE_GROUP_ORDER, FILE_TYPE_LABEL, formatFileSize } from '../lib/file-labels';
import {
  RECRUITMENT_FILE_MAX_SIZE_BYTES,
  RECRUITMENT_PARSER_EXTENSION_REGEX,
  RECRUITMENT_PARSER_MIME_TYPES,
  RECRUITMENT_UPLOAD_EXTS_BY_SLOT,
  RECRUITMENT_UPLOAD_HINT_BY_SLOT,
  type RecruitmentFileSlot,
} from '@hygge/shared';

const MAX_FILE_SIZE_BYTES = RECRUITMENT_FILE_MAX_SIZE_BYTES;
const PARSEABLE_MIMES = new Set<string>(RECRUITMENT_PARSER_MIME_TYPES);
const PARSEABLE_EXT = RECRUITMENT_PARSER_EXTENSION_REGEX;

function isParseable(file: File): boolean {
  return PARSEABLE_MIMES.has(file.type) || PARSEABLE_EXT.test(file.name);
}

interface CandidateFileUploadDialogProps {
  candidateId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultType?: CandidateFileType;
}

export function CandidateFileUploadDialog({
  candidateId,
  open,
  onOpenChange,
  defaultType = 'CV',
}: CandidateFileUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<CandidateFileType>(defaultType);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadCandidateFile(candidateId);
  const parseCv = useParseCvForCandidate(candidateId);
  const { startParseJob } = useCandidateParseJob();
  const isPending = upload.isPending || parseCv.isPending;

  const slotKey = fileType as RecruitmentFileSlot;
  const slotAccept = RECRUITMENT_UPLOAD_EXTS_BY_SLOT[slotKey].join(',');
  const slotHint = RECRUITMENT_UPLOAD_HINT_BY_SLOT[slotKey];

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileType(defaultType);
      setError(null);
      setIsDragging(false);
      if (inputRef.current) inputRef.current.value = '';
    } else {
      setFileType(defaultType);
    }
  }, [open, defaultType]);

  const applyFile = (next: File | null) => {
    setError(null);
    if (next && next.size > MAX_FILE_SIZE_BYTES) {
      setError(`File too large (max ${formatFileSize(MAX_FILE_SIZE_BYTES)}).`);
      setFile(null);
      // Clear the native input so the user can pick the same file again after
      // e.g. resizing it — otherwise onChange won't fire for an identical value.
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(next);
  };

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyFile(e.target.files?.[0] ?? null);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setError(null);
    try {
      if (fileType === 'CV' && isParseable(file)) {
        const { parseJobId } = await parseCv.mutateAsync(file);
        startParseJob(parseJobId);
      } else {
        await upload.mutateAsync({ file, fileType });
      }
      onOpenChange(false);
    } catch (err) {
      setError(extractErrorMessage(err, 'Upload failed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Upload file</DialogTitle>
          <DialogDescription>
            Max {formatFileSize(MAX_FILE_SIZE_BYTES)}. New uploads create a new version.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="file-type">File type</Label>
            <Select
              value={fileType}
              onValueChange={(v) => setFileType(v as CandidateFileType)}
            >
              <SelectTrigger id="file-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILE_TYPE_GROUP_ORDER.map((t) => (
                  <SelectItem key={t} value={t}>
                    {FILE_TYPE_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="file-input">File</Label>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragEnter={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                applyFile(e.dataTransfer.files?.[0] ?? null);
              }}
              className={cn(
                'flex w-full items-center gap-3 rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-left text-sm transition-colors hover:bg-muted/40',
                file ? 'border-solid bg-background' : '',
                isDragging && 'border-hygge bg-hygge/10',
              )}
            >
              <UploadCloud className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                {file ? (
                  <>
                    <div className="truncate font-medium text-foreground" title={file.name}>
                      {file.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-medium text-foreground">Click to choose a file</div>
                    <div className="text-xs text-muted-foreground">
                      {slotHint}
                    </div>
                  </>
                )}
              </div>
            </button>
            <input
              ref={inputRef}
              id="file-input"
              type="file"
              className="hidden"
              onChange={handlePick}
              accept={slotAccept}
            />
          </div>

          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="accent"
            onClick={handleSubmit}
            disabled={!file || isPending}
          >
            {isPending ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
